import { useState, useCallback } from 'react';
import { getAuthorized, postAuthorized } from './User';
import config from '../Config';

/**
 * Хук для работы с API чата врача-пациента (RAG-анализ)
 *
 * Функции:
 * - fetchSessions(patientId) — GET /api/doctor/patients/{id}/chat/sessions
 * - createSession(patientId) — POST /api/doctor/patients/{id}/chat/sessions
 * - fetchMessages(patientId, sessionId) — GET /api/doctor/patients/{id}/chat/sessions/{sessionId}
 * - sendMessage(patientId, sessionId, content) — POST /api/doctor/patients/{id}/chat/sessions/{sessionId}/messages
 */
export default function usePatientChat() {
    const [sessions, setSessions] = useState([]);
    const [messages, setMessages] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);

    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    const [sessionsError, setSessionsError] = useState(null);
    const [messagesError, setMessagesError] = useState(null);
    const [sendMessageError, setSendMessageError] = useState(null);

    const baseUrl = config.getApiBaseUrl();

    const fetchSessions = useCallback(async (patientId) => {
        setSessionsLoading(true);
        setSessionsError(null);
        setMessages([]);
        setCurrentSessionId(null);
        try {
            const url = `${baseUrl}/doctor/patient_chat/sessions?patient_id=${patientId}`;
            const response = await getAuthorized(url);
            if (response.data?.has_error) {
                setSessionsError(
                    response.data.error_description || 'Ошибка при загрузке сессий чата'
                );
                setSessions([]);
                return;
            }
            setSessions(response.data?.sessions || []);
        } catch (error) {
            console.error('Failed to fetch chat sessions:', error);
            setSessionsError(
                error.response?.data?.error_description || 'Ошибка при загрузке сессий чата'
            );
            setSessions([]);
        } finally {
            setSessionsLoading(false);
        }
    }, [baseUrl]);

    const createSession = useCallback(async (patientId) => {
        setSessionsLoading(true);
        setSessionsError(null);
        try {
            const url = `${baseUrl}/doctor/patient_chat/sessions?patient_id=${patientId}`;
            const response = await postAuthorized(url);
            if (response.data?.has_error) {
                setSessionsError(
                    response.data.error_description || 'Ошибка при создании сессии'
                );
                return null;
            }
            const newSession = response.data?.session;
            setSessions((prev) => [newSession, ...prev]);
            return newSession;
        } catch (error) {
            console.error('Failed to create chat session:', error);
            setSessionsError(
                error.response?.data?.error_description || 'Ошибка при создании сессии'
            );
            return null;
        } finally {
            setSessionsLoading(false);
        }
    }, [baseUrl]);

    const fetchMessages = useCallback(async (patientId, sessionId) => {
        setCurrentSessionId(sessionId);
        setMessagesLoading(true);
        setMessagesError(null);
        try {
            const url = `${baseUrl}/doctor/patient_chat/sessions/${sessionId}?patient_id=${patientId}`;
            const response = await getAuthorized(url);
            if (response.data?.has_error) {
                setMessagesError(
                    response.data.error_description || 'Ошибка при загрузке сообщений'
                );
                setMessages([]);
                return;
            }
            setMessages(response.data?.messages || []);
        } catch (error) {
            console.error('Failed to fetch chat messages:', error);
            setMessagesError(
                error.response?.data?.error_description || 'Ошибка при загрузке сообщений'
            );
            setMessages([]);
        } finally {
            setMessagesLoading(false);
        }
    }, [baseUrl]);

    const sendMessage = useCallback(async (patientId, sessionId, content) => {
        setSendingMessage(true);
        setSendMessageError(null);
        try {
            // Optimistically add user message
            const userMsg = {
                id: Date.now(),
                role: 'user',
                content,
                creation_dt: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, userMsg]);

            const url = `${baseUrl}/doctor/patient_chat/sessions/${sessionId}/messages?patient_id=${patientId}`;
            const response = await postAuthorized(url, { content });
            if (response.data?.has_error) {
                setSendMessageError(
                    response.data.error_description || 'Ошибка при отправке сообщения'
                );
                return null;
            }
            const newMessage = response.data?.message;
            if (newMessage) {
                setMessages((prev) => [...prev, newMessage]);
            }
            if (response.data?.messages) {
                setMessages((prev) => [...prev, ...response.data.messages]);
            }
            return response.data;
        } catch (error) {
            console.error('Failed to send message:', error);
            setSendMessageError(
                error.response?.data?.error_description || 'Ошибка при отправке сообщения'
            );
            return null;
        } finally {
            setSendingMessage(false);
        }
    }, [baseUrl]);

    return {
        sessions,
        messages,
        currentSessionId,
        sessionsLoading,
        messagesLoading,
        sendingMessage,
        sessionsError,
        messagesError,
        sendMessageError,
        fetchSessions,
        createSession,
        fetchMessages,
        sendMessage,
    };
}
